import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	Outlet,
	useNavigate,
	useParams,
	useRouterState,
	useSearch,
} from "@tanstack/react-router";
import { useContext } from "react";
import z from "zod";
import { EMPTY_ORG_ID } from "@/constants/org";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useUpdateJobType } from "@/services/job/job.mutations";
import { jobQueryOptions } from "@/services/job/job.options";
import { JobTypeDrawerContext } from "@/utils/job-type-detail/job-type-drawer-context";
import { showSuccessToast } from "@/utils/toast.utils";
import type { AdminJobTypeFullPageTab } from "./AdminJobTypeFullPage";
import { AdminJobTypeFullPage } from "./AdminJobTypeFullPage";

// const FULL_PARENT =
// 	"/_authenticated/(admin-app)/_admin/admin/configuration/jobTypes/$jobTypeId/full" as const;

const SearchSchema = z.object({
	tab: z
		.enum(["details", "form", "lifecycle", "rules", "activity"])
		.optional()
		.default("details"),
	mode: z.enum(["view", "edit"]).optional().default("view"),
	presentation: z.enum(["drawer", "modal", "full"]).optional().default("full"),
	ruleId: z.string().optional(),
	/** Editor open mode for the rule drawer on `/full/form/rules`. */
	ruleMode: z.enum(["create", "edit", "delete"]).optional(),
	/** Sub-tab inside a single rule's modal/full view. Currently unused on form rules. */
	ruleTab: z.enum(["details", "activity"]).optional(),
});

const FULL_PARENT = "/admin/configuration/jobTypes/$jobTypeId/full" as const;

export const Route = createFileRoute(
	"/_authenticated/(admin-app)/_admin/admin/configuration/jobTypes/$jobTypeId/full",
)({
	component: AdminJobTypeFullView,
	validateSearch: (search) => SearchSchema.parse(search),
});

function AdminJobTypeFullView() {
	const navigate = useNavigate();
	const { t } = useAppTranslation();
	const params = useParams({
		from: "/_authenticated/(admin-app)/_admin/admin/configuration/jobTypes/$jobTypeId/full",
	});
	const search = useSearch({
		from: "/_authenticated/(admin-app)/_admin/admin/configuration/jobTypes/$jobTypeId/full",
	});
	const pathname = useRouterState({ select: (s) => s.location.pathname });

	const detailQuery = useQuery(jobQueryOptions.jobTypeDetail(params.jobTypeId));
	const jobType = detailQuery.data ?? null;
	const drawerCtx = useContext(JobTypeDrawerContext);
	const updateMutation = useUpdateJobType();

	const isEditMode = search.mode === "edit";

	const formSegment = `/configuration/jobTypes/${params.jobTypeId}/full/form`;
	const onFormRoutes = pathname.includes(formSegment);
	const rulesSegment = `/configuration/jobTypes/${params.jobTypeId}/full/rules/`;
	const onRuleDetailRoute = pathname.includes(rulesSegment);

	const activeTab: AdminJobTypeFullPageTab = onFormRoutes ? "form" : search.tab;

	if (onRuleDetailRoute || onFormRoutes) {
		return <Outlet />;
	}

	const handleTabChange = (tab: AdminJobTypeFullPageTab) => {
		if (tab === "form") {
			void navigate({
				to: FULL_PARENT,
				params: { jobTypeId: params.jobTypeId },
				search: {
					tab: "form",
					mode: search.mode,
					presentation: search.presentation,
				},
			});
			return;
		}
		void navigate({
			to: FULL_PARENT,
			params: { jobTypeId: params.jobTypeId },
			search: { tab, mode: search.mode, presentation: search.presentation },
		});
	};

	const handleBackToList = () => {
		void navigate({
			to: "/admin/configuration/jobTypes",
		});
	};

	if (!jobType && !detailQuery.isLoading && !detailQuery.isError) {
		return null;
	}

	if (!jobType) {
		return (
			<div className="flex min-h-screen items-center justify-center">
				<div className="text-sm text-gray-500">
					{detailQuery.isLoading
						? t("common.actions.loadingEllipsis")
						: t("admin.jobTypes.drawer.errors.notFound")}
				</div>
			</div>
		);
	}

	const fullPageContext = drawerCtx
		? {
				...drawerCtx,
				mode: (search.mode ?? "view") as "view" | "edit",
				setMode: (mode: "view" | "edit") => {
					void navigate({
						to: FULL_PARENT,
						params: { jobTypeId: params.jobTypeId },
						search: { tab: "details", mode, presentation: search.presentation },
					});
				},
				isSaving: updateMutation.isPending,
				saveDraft: async () => {
					if (!drawerCtx.isValid) return;
					try {
						await updateMutation.mutateAsync({
							jobTypeId: params.jobTypeId,
							data: {
								name: drawerCtx.draftName.trim(),
								description: drawerCtx.draftDescription || null,
								icon: drawerCtx.draftIcon || null,
								typeId: params.jobTypeId,
								statusLifecycleId: drawerCtx.jobType.lifecycleId ?? null,
								locationRequired: drawerCtx.locationRequired,
							},
							organizationId: EMPTY_ORG_ID,
						});
						showSuccessToast({
							message: t("admin.jobTypes.drawer.details.toast.saveSuccess"),
						});
						void navigate({
							to: FULL_PARENT,
							params: { jobTypeId: params.jobTypeId },
							search: {
								tab: "details",
								mode: "view" as const,
								presentation: search.presentation,
							},
						});
					} catch {
						// Global mutation error handler in root-provider.tsx already shows the error toast
					}
				},
				cancelDraft: () => {
					drawerCtx.setDraftName(drawerCtx.jobType.name ?? "");
					drawerCtx.setDraftDescription(drawerCtx.jobType.description ?? "");
					drawerCtx.setDraftIcon(drawerCtx.jobType.icon ?? "");
					void navigate({
						to: FULL_PARENT,
						params: { jobTypeId: params.jobTypeId },
						search: {
							tab: "details",
							mode: "view" as const,
							presentation: search.presentation,
						},
					});
				},
			}
		: null;

	return (
		<JobTypeDrawerContext.Provider value={fullPageContext}>
			<AdminJobTypeFullPage
				jobType={jobType}
				activeTab={activeTab}
				onTabChange={handleTabChange}
				onBackToList={handleBackToList}
				isLoading={detailQuery.isLoading}
				isError={detailQuery.isError}
				isEditMode={isEditMode}
			>
				<Outlet />
			</AdminJobTypeFullPage>
		</JobTypeDrawerContext.Provider>
	);
}
